import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useLocation } from "react-router-dom";

import { DESKTOP_QUERY } from "@/components/layout/layout.constants";
import { usePermissions } from "@/hooks/usePermissions";
import { usePrefersReducedMotion } from "@/hooks/usePrefersReducedMotion";
import { PLATFORM_SECTION_ENUM } from "@/utils/enums";

import { MayaWarpOverlay } from "../components/maya-warp";
import {
  KEYBOARD_SHORTCUT_KEY,
  PAGE_LENS_ENABLED,
  STORAGE_KEYS,
} from "../constants";
import { useKeyboardShortcut } from "../hooks/use-keyboard-shortcut";
import { MAYA_SPACES, type MayaSpace } from "../spaces";
import { isAssistantPage } from "../utils/assistant-page";
import {
  localGet,
  localSet,
  sessionGet,
  sessionSet,
} from "../utils/safe-storage";
import { pingComposer } from "../utils/composer-ping";
import { centreOf } from "../utils/warp";

import {
  MayaAssistantComposerContext,
  MayaSidebarContext,
  type MayaAssistantComposer,
  type MayaSidebarContextValue,
  type MayaWarp,
} from "./sidebar-context";

interface MayaSidebarProviderProps {
  children: ReactNode;
}

/** Each space's full AI Assistant page. */
const ASSISTANT_ROOTS = Object.values(MAYA_SPACES).map((s) => s.fullPage(null));

/**
 * Open/closed state shared by the floating launcher (portaled to body) and
 * the chat sidebar (in the layout row beside the page). Lives in the app
 * `Layout`, so the sidebar stays open while the user moves between pages.
 *
 * On the full AI Assistant page Maya is already open, so the sidebar closes
 * there (its stars flying into the page's composer), and tapping the
 * launcher just says Maya is already here.
 */
export const MayaSidebarProvider = ({ children }: MayaSidebarProviderProps) => {
  const { pathname } = useLocation();
  const onAssistantPage = isAssistantPage(pathname, ASSISTANT_ROOTS);

  // Workspace pages get Workspace Maya — for users allowed to chat there;
  // everyone else keeps their personal Maya. Assumed allowed while the
  // permissions load, so the chat doesn't swap spaces under most users.
  const { can, isLoading: permissionsLoading } = usePermissions();
  const space: MayaSpace =
    pathname.startsWith("/company") &&
    (permissionsLoading || can(PLATFORM_SECTION_ENUM.AI_ASSISTANT, "create"))
      ? "company"
      : "my-space";

  // Reopens after a reload in the same tab, like the chat it holds — on
  // desktop only, where it sits beside the page. Below lg it is a drawer over
  // the page, which must never cover content on first load. Never on the AI
  // Assistant page, where Maya is the page.
  const [open, setOpen] = useState(
    () =>
      !onAssistantPage &&
      sessionGet(STORAGE_KEYS.sidebarOpen) === "1" &&
      typeof window !== "undefined" &&
      window.matchMedia(DESKTOP_QUERY).matches,
  );
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const headerOrbRef = useRef<HTMLSpanElement | null>(null);
  const sidebarRef = useRef<HTMLElement | null>(null);
  const returnFocusRef = useRef(false);
  const focusComposerRef = useRef(false);
  const assistantComposerRef = useRef<MayaAssistantComposer | null>(null);
  const staleChatRef = useRef(false);
  const reducedMotion = usePrefersReducedMotion();

  useEffect(() => {
    sessionSet(STORAGE_KEYS.sidebarOpen, open ? "1" : "0");
  }, [open]);

  // Every open/close goes through here so it can launch the warp from
  // wherever Maya is right now — the launcher when opening, the header
  // avatar when closing. Positions are read before the state flips, while
  // both ends are still where the user last saw them.
  const [warp, setWarp] = useState<MayaWarp | null>(null);
  const warpIdRef = useRef(0);

  const setOpenWithWarp = useCallback(
    (next: boolean, intoPage = false) => {
      if (next === open) return;
      if (next) {
        focusComposerRef.current = true;
      } else {
        // Moving into the AI Assistant page, focus belongs to its composer.
        const active = document.activeElement;
        returnFocusRef.current =
          !intoPage &&
          (!active ||
            active === document.body ||
            !!sidebarRef.current?.contains(active));
      }
      const from = centreOf(next ? triggerRef.current : headerOrbRef.current);
      setWarp(
        from && !reducedMotion
          ? {
              id: ++warpIdRef.current,
              direction: next ? "in" : intoPage ? "page" : "out",
              from,
              phase: "travelling",
            }
          : null,
      );
      setOpen(next);
    },
    [open, reducedMotion],
  );
  const toggle = useCallback(
    () => setOpenWithWarp(!open),
    [open, setOpenWithWarp],
  );
  const close = useCallback(() => setOpenWithWarp(false), [setOpenWithWarp]);

  const landWarp = useCallback(
    (id: number) =>
      setWarp((w) => (w?.id === id ? { ...w, phase: "landed" } : w)),
    [],
  );
  const clearWarp = useCallback(
    (id: number) => setWarp((w) => (w?.id === id ? null : w)),
    [],
  );

  // Arriving on the AI Assistant page: the sidebar steps into the page, and
  // its chat may move on there, so it is re-read when the sidebar reopens.
  const arrivedRef = useRef(false);
  useEffect(() => {
    if (!onAssistantPage) {
      arrivedRef.current = false;
      return;
    }
    if (arrivedRef.current) return;
    arrivedRef.current = true;
    staleChatRef.current = true;
    if (open) setOpenWithWarp(false, true);
  }, [onAssistantPage, open, setOpenWithWarp]);

  // On the AI Assistant page ⌘K means "talk to Maya" — already here, in the
  // page's composer.
  const focusAssistantComposer = useCallback(() => {
    const input = assistantComposerRef.current?.inputRef.current;
    if (!input) return;
    input.focus();
    if (!reducedMotion) pingComposer(input);
  }, [reducedMotion]);

  // Sharing the open page is on by default and remembered per browser. It
  // lives here, not in the chat, because the page itself shows it too (the
  // light seam on its edge — see MayaPageSeam). While the lens is hidden
  // the page is always shared, and the stored choice is left as it was.
  const [shareChoice, setSharePageContext] = useState(
    () => localGet(STORAGE_KEYS.sharePageContext) !== "0",
  );
  useEffect(() => {
    localSet(STORAGE_KEYS.sharePageContext, shareChoice ? "1" : "0");
  }, [shareChoice]);
  const sharePageContext = !PAGE_LENS_ENABLED || shareChoice;
  const toggleSharePageContext = useCallback(
    () => setSharePageContext((prev) => !prev),
    [],
  );

  useKeyboardShortcut({
    key: KEYBOARD_SHORTCUT_KEY,
    meta: true,
    onTrigger: onAssistantPage ? focusAssistantComposer : toggle,
  });

  const value = useMemo<MayaSidebarContextValue>(
    () => ({
      open,
      toggle,
      close,
      triggerRef,
      headerOrbRef,
      sidebarRef,
      returnFocusRef,
      focusComposerRef,
      sharePageContext,
      toggleSharePageContext,
      warp,
      landWarp,
      clearWarp,
      space,
      onAssistantPage,
      assistantComposerRef,
      staleChatRef,
    }),
    [
      open,
      toggle,
      close,
      sharePageContext,
      toggleSharePageContext,
      warp,
      landWarp,
      clearWarp,
      space,
      onAssistantPage,
    ],
  );

  return (
    <MayaSidebarContext.Provider value={value}>
      <MayaAssistantComposerContext.Provider value={assistantComposerRef}>
        {children}
      </MayaAssistantComposerContext.Provider>
      <MayaWarpOverlay />
    </MayaSidebarContext.Provider>
  );
};
